import './BillingIssuesBadge.scss';
import React from 'react';
import { Tooltip } from 'primereact/tooltip';
import { useBillingIssues } from '../../../hooks/billing/UseBillingIssues';

interface BillingIssuesBadgeProps {
  /** Unique element id — used as the tooltip target. */
  targetId: string;
  /** Tooltip direction (PrimeReact position). */
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  /** Optional extra class for context-specific positioning tweaks. */
  className?: string;
}

/**
 * A small red count badge for billing "things to fix" (expiring card / upcoming payment),
 * with a tooltip listing them. Self-contained: reads `useBillingIssues` and renders nothing
 * when there is nothing to fix. Positions itself in the top-right corner of its (relatively
 * positioned) parent. Used on the Subscription settings tab and the login-box Settings item.
 */
const BillingIssuesBadge: React.FC<BillingIssuesBadgeProps> = ({
  targetId,
  tooltipPosition = 'right',
  className = '',
}) => {
  const { count, messages } = useBillingIssues();

  if (count === 0) {
    return null;
  }

  return (
    <>
      <span id={targetId} className={`billing-badge ${className}`}>
        {count}
      </span>
      <Tooltip
        target={`#${targetId}`}
        position={tooltipPosition}
        appendTo={document.body}
        className={`tooltip--${tooltipPosition} billing-badge__tooltip`}
      >
        {messages.map((message, index) => (
          <div key={index} className="billing-badge__tooltip-line">
            {message}
          </div>
        ))}
      </Tooltip>
    </>
  );
};

export default BillingIssuesBadge;
